import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { formatMoney } from '../../util/currency';
import { richText } from '../../util/richText';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { isIdentityVerified } from '../../util/diditVerification';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const MAX_BIO_LENGTH = 140;

const stripHtmlToPlain = text => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  return text
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const truncateText = (text, maxLen) => {
  if (!text || text.length <= maxLen) {
    return text;
  }
  return `${text.slice(0, maxLen - 1).trim()}…`;
};

/**
 * The place of a listing as "City, State": a full address ("12 Main St, Franklin, Tennessee,
 * United States of America") shows only its last two parts, without the country.
 *
 * @param {string} address
 * @returns {string}
 */
export const shortPlaceLabel = address => {
  if (typeof address !== 'string') {
    return '';
  }
  const parts = address
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  const isCountry = part => /^(united states|usa|us)\b/i.test(part);
  const withoutCountry =
    parts.length > 2 && isCountry(parts[parts.length - 1]) ? parts.slice(0, -1) : parts;
  return withoutCountry.slice(-2).join(', ');
};

const stringList = value =>
  Array.isArray(value) ? value.filter(v => typeof v === 'string' && v.length > 0) : [];

/**
 * The sitter's animals as labels: the listing's "animals" field stores option values
 * ("dairy_cows"), shown with the labels of the listing field's options ("Dairy Cows"). Listings
 * without animals fall back to their skills or tags.
 *
 * @param {Object} publicData listing's public data
 * @param {Array<Object>?} listingFields the marketplace's listing field configs
 * @returns {Array<string>}
 */
export const animalLabels = (publicData, listingFields) => {
  const pd = publicData || {};
  const animals = stringList(pd.animals);
  if (animals.length > 0) {
    const field = (listingFields || []).find(f => f.key === 'animals');
    const labelOf = value => field?.enumOptions?.find(o => `${o.option}` === value)?.label || value;
    return animals.map(labelOf);
  }
  const skills = stringList(pd.skills);
  return skills.length > 0 ? skills : stringList(pd.tags);
};

/**
 * Optional display fields for the listing card (location, tags, rating, bio excerpt).
 * Rating/review counts may come from listing or author publicData when provided via Console or Integration API.
 *
 * @param {Object} listing - listing or ownListing
 * @param {boolean} showAuthorInfo - when true, headline prefers author display name
 * @param {Array<Object>?} listingFields - the marketplace's listing field configs
 * @returns {Object} display data for the card body
 */
export const getListingCardDisplayData = (listing, showAuthorInfo, listingFields) => {
  const { description = '', publicData } = listing?.attributes || {};
  const author = listing?.author;
  const authorProfile = author?.attributes?.profile;
  const authorPublicData = authorProfile?.publicData || {};
  const listingPd = publicData || {};

  const displayName = authorProfile?.displayName;
  const headlineIsAuthor = showAuthorInfo && displayName;

  const plainDescription = stripHtmlToPlain(description);
  const bioExcerpt = truncateText(plainDescription, MAX_BIO_LENGTH);

  const address = listingPd?.location?.address;
  const locationLabel = shortPlaceLabel(address);

  const tags = animalLabels(listingPd, listingFields).slice(0, 5);

  // Verified through Didit. This comes from the author's metadata, which only the server can write
  // (see server/api/didit-webhook.js). Public data is not used: users can edit their own.
  const showVerified = isIdentityVerified(author);

  const aggregateRatingStored = authorPublicData.aggregateRating;
  const authorAggregateRating = aggregateRatingStored > 0 ? aggregateRatingStored / 100 : null;

  return {
    headlineIsAuthor,
    bioExcerpt,
    locationLabel,
    tags,
    showVerified,
    authorAggregateRating,
  };
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTooltip: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/**
 * Returns all translated and formatted strings for ListingCard so the
 * presentational component can stay simple and aria-labels use the same copy.
 *
 * @param {Object} listing - API entity: listing or ownListing
 * @param {Object} config - app configuration (e.g. from useConfiguration())
 * @param {Object} intl - React Intl instance (e.g. from useIntl())
 * @returns {Object} translations and derived values:
 *   - titlePlain: raw title string (for aria/alt)
 *   - titleFormatted: React nodes from richText(title) for display
 *   - showPrice: whether to show the price block
 *   - priceTooltip: string for the price element's title attribute (tooltip on hover)
 *   - priceMessage: React nodes for the price block content (styled amount + per-unit)
 *   - cardAriaLabel: ready-to-use aria-label for the card link (listing title + plain price line when shown)
 *   - authorName: "ListingCard.author" string containing author's display name
 */
export const getListingCardTranslations = (listing, config, intl) => {
  const { title = '', price, publicData } = listing?.attributes || {};

  const authorDisplayName = listing?.author?.attributes?.profile?.displayName;
  const authorName = intl.formatMessage(
    { id: 'ListingCard.author' },
    { authorName: authorDisplayName }
  );

  const validListingTypes = config.listing.listingTypes || [];
  const { listingType } = publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(listingTypeConfig);
  const { formattedPrice, priceTooltip } = priceData(price, config.currency, intl);

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  const priceMessageId = hasMultiplePriceVariants
    ? 'ListingCard.priceStartingFrom'
    : 'ListingCard.price';

  const perUnitString = isBookable
    ? intl.formatMessage({ id: 'ListingCard.perUnit' }, { unitType: publicData?.unitType })
    : '';

  // Visible price block uses JSX spans for styling; aria-label needs a plain string.
  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? <span className={css.perUnit}>{perUnitString}</span> : '';
  const priceMessage =
    showPrice && formattedPrice != null
      ? intl.formatMessage({ id: priceMessageId }, { priceValue, pricePerUnit })
      : '';

  const priceMessagePlain =
    showPrice && formattedPrice != null
      ? intl.formatMessage(
          { id: priceMessageId },
          { priceValue: formattedPrice, pricePerUnit: perUnitString }
        )
      : '';

  const cardAriaLabel =
    priceMessagePlain.length > 0
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.label' },
          { listingTitle: title, formattedPrice: priceMessagePlain }
        )
      : title;

  return {
    titlePlain: title,
    titleFormatted: richText(title, {
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    }),
    authorName,
    showPrice,
    priceTooltip,
    priceMessage,
    cardAriaLabel,
  };
};
